// v1.32: 询盘详情页 — 桌面: 顶摘要 + 操作 + 草稿/历史/原文 三段; 移动: tab 切换
// v1.22 起: 打开 + 查看 + 回复 (AI 起草 / 标记已回复 / 飞书草稿 / 回复历史)
// v1.27 起: 一键加客户 + 飞书草稿时间线 + 回复消息 thread
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { LarkMailEntry } from '../components/LarkInbox';
import { Badge, Button, Card, TabPanel, Tabs } from '../components/ui';
import { type Inquiry, inquiryApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

/** v1.37.1: ken@toei-sosan.com 的收件/发件都在 Lark Mail（MX → larksuite.com） */
const LARK_MAIL_WEB_URL = 'https://mail.larksuite.com/';

export const Route = createFileRoute('/inquiries/$inquiryId')({
  component: InquiryDetailPage,
});

type Language = 'ja' | 'en' | 'zh';
type TabKey = 'compose' | 'history' | 'original';

function formatDate(ms: number) {
  return new Date(ms).toLocaleString('zh-CN');
}

/**
 * v1.37.12 修 mailto 编码
 *
 * bug: 原来用 `URLSearchParams({ subject, body }).toString()` — 它按
 * `application/x-www-form-urlencoded` 编码, 空格 → `+`、换行 → `%0A`。
 * 部分邮件客户端 (macOS Mail / Gmail web) 把 `+` 当成字面加号原样显示,
 * 结果客户收到的正文是:
 *   `Dear+Studio34+Design+Pvt+Ltd.+++Thank+you+for+...`
 * 换行全丢了, 一整封挤成一段。
 *
 * 修法: 改用 encodeURIComponent (RFC 3986 percent-encoding)
 *   - 空格 → %20 (不是 +)
 *   - 换行 → %0A (正常被客户端解析成换行)
 *   - 保留换行, 不 collapse 成一行
 */
function buildMailto(opts: { to: string; subject: string; body: string }) {
  // 主题里的换行/回车会让部分客户端解析失败, 先归一成单空格
  const subject = opts.subject.replace(/[\r\n]+/g, ' ').trim();
  // 正文: 保留换行结构, 只去掉可能破坏 header 的孤立 CR
  const body = opts.body.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return (
    `mailto:${encodeURIComponent(opts.to).replace(/%40/g, '@')}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

function InquiryDetailPage() {
  const { inquiryId } = Route.useParams();
  const qc = useQueryClient();
  // v1.36+: 显示当前登录账号 (邮件客户端默认 FROM)
  const currentUserEmail = useAuthStore((s) => s.user?.email);
  const { data, isLoading, error } = useQuery({
    queryKey: ['inquiry', inquiryId],
    queryFn: () => inquiryApi.get(inquiryId),
  });
  const [draftLang, setDraftLang] = useState<Language>('ja');
  const [draftTone, setDraftTone] = useState<'professional' | 'friendly'>('professional');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [tab, setTab] = useState<TabKey>('compose');

  // v1.36+: 起草中文对照 (Ken 不会日文, AI 输出日文需看中文)
  const [draftChineseRef, setDraftChineseRef] = useState<{ subject: string; body: string } | null>(
    null,
  );
  // v1.36+: "再翻译" — 把 editedBody 翻回 draftLang (AI 起草时用的语言)
  const retransMut = useMutation({
    mutationFn: ({ target, text }: { target: 'ja' | 'en' | 'zh'; text: string }) =>
      inquiryApi.translateText(inquiryId, { target, text }),
    onSuccess: (res) => {
      if (res.ok && res.translated) {
        setEditedBody(res.translated);
        setToast({
          kind: 'success',
          msg: `已重新翻译成${res.target === 'ja' ? '日文' : res.target === 'en' ? '英文' : '中文'}`,
        });
      } else {
        setToast({ kind: 'error', msg: res.error ?? '翻译失败' });
      }
    },
    onError: (e) => setToast({ kind: 'error', msg: `再翻译失败: ${(e as Error).message}` }),
  });
  const draftMut = useMutation({
    mutationFn: () => inquiryApi.replyDraft(inquiryId, { language: draftLang, tone: draftTone }),
    onSuccess: (d) => {
      setEditedSubject(d.subject);
      setEditedBody(d.body);
      // v1.36+: 中文对照 = 翻译 AI 起草的 d.body (不是 inquiry.body!)
      // 这样 Ken 才能看懂 AI 写了什么日文
      const aiDraftBody = d.body;
      const aiDraftSubject = d.subject;
      if (draftLang !== 'zh') {
        // 翻译 AI 起草的 body 和 subject 各一次 (用 translate-text endpoint)
        Promise.all([
          inquiryApi.translateText(inquiryId, { target: 'zh', text: aiDraftBody }),
          aiDraftSubject
            ? inquiryApi.translateText(inquiryId, { target: 'zh', text: aiDraftSubject })
            : null,
        ])
          .then(([bodyRes, subjRes]) => {
            setDraftChineseRef({
              subject: subjRes?.ok ? subjRes.translated : '',
              body: bodyRes.ok ? bodyRes.translated : '',
            });
          })
          .catch((e) => {
            // 失败也不影响起草, 显示原文
            setDraftChineseRef({ subject: aiDraftSubject, body: aiDraftBody });
          });
      } else {
        // 起草已经是中文, 中文对照就是原文
        setDraftChineseRef({ subject: aiDraftSubject, body: aiDraftBody });
      }
      setToast({ kind: 'success', msg: `AI 起草完成 (${d.generatedBy})` });
      setTab('compose');
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });
  // v1.36+: 监听 editedBody 变化, 自动重新翻译中文对照 (防抖 1.5s)
  // 当用户修改 AI 起草的日文后, 中英对照会自动更新
  const [autoTranslateBusy, setAutoTranslateBusy] = useState(false);
  useEffect(() => {
    if (!editedBody || draftLang === 'zh') return;
    if (!draftChineseRef) return; // 首次翻译还没完成前不触发

    const timer = setTimeout(async () => {
      // 防抖: 1.5s 没新输入才翻译
      setAutoTranslateBusy(true);
      try {
        const [bodyRes, subjRes] = await Promise.all([
          editedBody
            ? inquiryApi.translateText(inquiryId, { target: 'zh', text: editedBody })
            : null,
          editedSubject
            ? inquiryApi.translateText(inquiryId, { target: 'zh', text: editedSubject })
            : null,
        ]);
        setDraftChineseRef({
          subject: subjRes?.ok ? subjRes.translated : '',
          body: bodyRes?.ok ? bodyRes.translated : editedBody,
        });
      } catch {
        // 静默失败, 保留旧翻译
      } finally {
        setAutoTranslateBusy(false);
      }
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedBody, editedSubject]);
  const markMut = useMutation({
    mutationFn: () => inquiryApi.markReplied(inquiryId, { method: 'email' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      qc.invalidateQueries({ queryKey: ['inquiries'] });
      qc.invalidateQueries({ queryKey: ['reply-messages', inquiryId] });
      setToast({ kind: 'success', msg: '已标记为已回复' });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });
  // v1.36+: 翻译 (Qwen3-30B)
  const [translatedBody, setTranslatedBody] = useState<{
    text: string;
    target: 'ja' | 'en' | 'zh';
    field: 'body' | 'subject';
  } | null>(null);
  // v1.36+: 主题翻译 (顶部摘要用, 跟 body 独立 state)
  const [subjectTranslation, setSubjectTranslation] = useState<{
    text: string;
    target: 'ja' | 'en' | 'zh';
  } | null>(null);
  const translateMut = useMutation({
    mutationFn: (params: { target: 'ja' | 'en' | 'zh'; field?: 'body' | 'subject' }) =>
      inquiryApi.translate(inquiryId, params),
    onSuccess: (res) => {
      if (res.ok) {
        setTranslatedBody({
          text: res.translated,
          target: res.target as 'ja' | 'en' | 'zh',
          field: res.field as 'body' | 'subject',
        });
        // v1.36+: 主题翻译额外存到 subjectTranslation (顶部摘要用)
        if (res.field === 'subject') {
          setSubjectTranslation({ text: res.translated, target: res.target as 'ja' | 'en' | 'zh' });
        }
        setToast({
          kind: 'success',
          msg: `已翻译成${res.target === 'ja' ? '日文' : res.target === 'en' ? '英文' : '中文'}`,
        });
      } else {
        setToast({ kind: 'error', msg: res.error ?? '翻译失败' });
      }
    },
    onError: (e) => setToast({ kind: 'error', msg: `翻译失败: ${(e as Error).message}` }),
  });
  // v1.37.12: 修复误删 — 上一轮清理 sendMut 时把 convertMut + 它的两个 useState 一起吞了
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertForm, setConvertForm] = useState({
    name: '',
    country: '',
    email: '',
    website: '',
    source: '',
    language: '',
  });
  function openConvertForm() {
    if (inquiry) {
      setConvertForm({
        name: inquiry.subject || '',
        country: inquiry.language === 'ja' ? 'JP' : inquiry.language === 'zh' ? 'CN' : 'US',
        email: '',
        website: '',
        source: inquiry.source,
        language: inquiry.language,
      });
      setShowConvertForm(true);
    }
  }
  const convertMut = useMutation({
    mutationFn: () =>
      inquiryApi.convertToCustomer(inquiryId, {
        name: convertForm.name,
        country: convertForm.country,
        email: convertForm.email || undefined,
        website: convertForm.website || undefined,
        source: convertForm.source,
        language: convertForm.language,
      } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      setShowConvertForm(false);
      setToast({ kind: 'success', msg: '已创建客户并关联' });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const { data: feishuDraftsData } = useQuery({
    queryKey: ['feishu-drafts', inquiryId],
    queryFn: () => inquiryApi.feishuDrafts(inquiryId),
    refetchInterval: 10000,
  });
  const { data: replyMessagesData } = useQuery({
    queryKey: ['reply-messages', inquiryId],
    queryFn: () => inquiryApi.replyMessages(inquiryId),
  });

  const feishuDrafts = feishuDraftsData?.items ?? [];
  const replyMessages = replyMessagesData?.items ?? [];
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-8 bg-surface-2 rounded w-2/3" />
        <div className="h-32 bg-surface-2 rounded" />
        <div className="h-48 bg-surface-2 rounded" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-surface border border-bad/40 rounded p-6 text-center">
        <Icon name="search-x" size="lg" className="text-bad mx-auto mb-2" />
        <p className="text-sm text-ink-2">询盘不存在或无权访问</p>
        <Link to="/inquiries" className="text-xs text-accent hover:underline mt-2 inline-block">
          ← 返回询盘列表
        </Link>
      </div>
    );
  }

  const inquiry = data as Inquiry & {
    customer?: {
      id: string;
      name: string;
      email: string | null;
      country: string;
      website?: string | null;
      tags?: string[] | null;
    };
  };
  const customer = inquiry.customer;
  const customerEmail = customer?.email;
  const hint = inquiry.productHint as
    | {
        category?: string;
        qty?: number;
        craft?: string[];
        urgency?: string;
        budget?: number | string;
        preferredContact?: string;
        rawText?: string;
      }
    | undefined;
  const mailto = customerEmail
    ? buildMailto({ to: customerEmail, subject: editedSubject, body: editedBody })
    : null;

  const statusVariant: 'info' | 'success' | 'warning' | 'neutral' =
    inquiry.status === 'new'
      ? 'info'
      : inquiry.status === 'rejected'
        ? 'warning'
        : inquiry.status === 'archived'
          ? 'neutral'
          : 'success';

  return (
    <div className="space-y-4">
      {/* === 顶部摘要: 客户 + 国家 + 状态 + 快速操作 === */}
      <Card padding="md" className="animate-fade-up">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 text-xs text-ink-3">
              <Link to="/inquiries" className="hover:text-accent">
                ← 询盘
              </Link>
              <span>/</span>
              {/* v1.36+: 完整 ID (之前 .slice(0,8) 截断, 现在显示完整 + mono 字体) */}
              <span className="font-mono truncate" title={inquiry.id}>
                {inquiry.id}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-ink break-words leading-tight flex-1 min-w-0">
              {subjectTranslation ? (
                <span title={`原文: ${inquiry.subject}`}>{subjectTranslation.text}</span>
              ) : (
                inquiry.subject || '(无主题)'
              )}
            </h1>
            {/* v1.36+: 主题翻译按钮组 (h1 右侧) */}
            {inquiry.subject && (
              <div className="inline-flex rounded border border-line bg-surface shrink-0">
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'zh', field: 'subject' })}
                  disabled={translateMut.isPending}
                  className="px-1.5 py-0.5 text-[10px] rounded-l hover:bg-surface-2 disabled:opacity-50"
                  title="翻译主题"
                >
                  🇨🇳
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'en', field: 'subject' })}
                  disabled={translateMut.isPending}
                  className="px-1.5 py-0.5 text-[10px] border-l border-line hover:bg-surface-2 disabled:opacity-50"
                  title="翻译主题"
                >
                  🇺🇸
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'ja', field: 'subject' })}
                  disabled={translateMut.isPending}
                  className="px-1.5 py-0.5 text-[10px] rounded-r border-l border-line hover:bg-surface-2 disabled:opacity-50"
                  title="翻译主题"
                >
                  🇯🇵
                </button>
              </div>
            )}
            {subjectTranslation && (
              <button
                type="button"
                onClick={() => setSubjectTranslation(null)}
                className="text-ink-3 hover:text-bad text-xs px-1 shrink-0"
                title="恢复原文"
              >
                ✕
              </button>
            )}
          </div>
          <Badge variant={statusVariant} size="md">
            {inquiry.status}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm">
          {customer ? (
            <>
              <Link
                to="/customers/$customerId"
                params={{ customerId: customer.id }}
                className="inline-flex items-center gap-1.5 font-medium text-ink hover:text-accent"
              >
                <Icon name="building-2" size="sm" className="text-accent" />
                {customer.name}
              </Link>
              <span className="text-xs text-ink-3">· {customer.country}</span>
              {/* v1.36+: 顶部摘要直接显示邮箱 (历史 tab 之外, 一眼看到联系信息) */}
              {customerEmail && (
                <a
                  href={`mailto:${customerEmail}`}
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline break-all"
                  title="发邮件"
                >
                  <Icon name="mail" size="xs" />
                  {customerEmail}
                </a>
              )}
              {customer?.tags && Array.isArray(customer.tags) && customer.tags.length > 0 && (
                <span className="inline-flex flex-wrap gap-1">
                  {customer.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-1.5 py-0.5 bg-surface-2 rounded text-[10px] text-ink-3"
                    >
                      #{tag}
                    </span>
                  ))}
                </span>
              )}
            </>
          ) : (
            <span className="text-ink-3 text-sm">未关联客户</span>
          )}
          <span className="text-xs text-ink-3 hidden sm:inline">
            · 📅 {formatDate(inquiry.receivedAt)}
          </span>
          <span className="text-xs text-ink-3 hidden sm:inline">
            · 🌐 {inquiry.language.toUpperCase()}
          </span>
          {/* v1.36+: source 来源 + sourceRef 提示 (邮件主题 / LinkedIn / 展会名) */}
          <span className="text-xs text-ink-3 hidden sm:inline">
            · 📥 {inquiry.source}
            {inquiry.sourceRef && (
              <span className="ml-1 text-ink-2 font-mono text-[10px]">
                ({inquiry.sourceRef.slice(0, 30)})
              </span>
            )}
          </span>
          {inquiry.assignedTo && (
            <span className="text-xs text-ink-3 hidden sm:inline">· 👤 {inquiry.assignedTo}</span>
          )}
          {inquiry.source === 'web3forms' && (
            <Badge variant="info" size="sm">
              🌐 官网表单
            </Badge>
          )}
        </div>

        <div className="sm:hidden text-xs text-ink-3 mt-1.5 flex items-center gap-2">
          <span>📅 {formatDate(inquiry.receivedAt)}</span>
          <span>🌐 {inquiry.language.toUpperCase()}</span>
        </div>

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mt-4">
          <Button
            variant="primary"
            size="sm"
            iconLeft="sparkles"
            onClick={() => draftMut.mutate()}
            loading={draftMut.isPending}
            className="sm:flex-1 sm:min-w-[140px]"
          >
            {draftMut.isPending ? '起草中' : 'AI 起草'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconLeft="user-plus"
            onClick={openConvertForm}
            loading={convertMut.isPending}
            disabled={!!customer}
            className="sm:flex-1 sm:min-w-[140px]"
          >
            {customer ? '已加客户' : '转客户'}
          </Button>
          {/* v1.37.12: 删掉「真发送」(Resend)。用户实测「一直错误码」——
              Resend 免费版硬限制: 只能发到注册 Resend 时用的邮箱 (kenblacke@gmail.com),
              验证了域名也发不了真实客户。1 人场景每天就发几封询盘回复,
              系统邮件客户端 (已配 Lark 的 ken@toei-sosan.com) 完全够用且零限制。
              自动化发送能力对当前场景是负资产, 移除后不再有"假成功"风险。
              同理删掉下面的 toOverride 输入框 (它只是为绕过 Resend 限制而存在的)。 */}
          {/* v1.37.12: 主发送路径 = 系统邮件客户端 (mailto:) */}
          {mailto && (
            <a
              href={mailto}
              className="w-full px-4 py-2 bg-good/10 text-good border border-good/30 rounded text-sm font-medium hover:bg-good/20 inline-flex items-center justify-center gap-1 mt-2"
            >
              📨 打开邮件客户端
            </a>
          )}
          {/* v1.37.12: 删掉「在 Lark Mail 里写」。用户反馈"基本不能打开 Lark Mail 网页",
              且系统邮件客户端已连上 Lark — 同一个动作两个入口, 反而让人不知道该点哪个。
              保留 mailto 作为辅助路径 + 复制按钮作为主路径 (v1.37.13)。 */}
          {/* v1.37.13: 只复制正文, 不带 "Subject: " 前缀。
              原来复制的是 `Subject: xxx\n\n正文`, 粘到邮件客户端正文里会多出一行
              "Subject: Re: ..." 垃圾。主题单独提供一个复制按钮。 */}
          {editedSubject && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(editedSubject);
                  setToast({ kind: 'success', msg: '📋 主题已复制' });
                } catch {
                  setToast({ kind: 'error', msg: '复制失败, 请手动选择复制' });
                }
              }}
              className="w-full px-4 py-2 bg-surface-2 border border-line rounded text-sm hover:bg-line/30"
            >
              📋 复制主题
            </button>
          )}
          {editedBody && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(editedBody);
                  setToast({ kind: 'success', msg: '📋 正文已复制（直接粘贴到邮件客户端）' });
                } catch {
                  setToast({ kind: 'error', msg: '复制失败, 请手动选择复制' });
                }
              }}
              className="w-full px-4 py-2 bg-surface-2 border border-line rounded text-sm hover:bg-line/30"
            >
              📋 复制正文
            </button>
          )}
          <Button
            variant="secondary"
            size="sm"
            iconLeft="check"
            onClick={() => markMut.mutate()}
            loading={markMut.isPending}
            className="sm:flex-1 sm:min-w-[140px] w-full mt-2"
          >
            标记已回复
          </Button>
        </div>
      </Card>

      <div className="sm:hidden">
        <Tabs
          value={tab}
          onChange={(v) => setTab(v as TabKey)}
          variant="pill"
          ariaLabel="询盘详情分区"
          items={[
            {
              value: 'compose',
              label: '草稿',
              icon: 'send',
              badge: feishuDrafts.length > 0 ? feishuDrafts.length : undefined,
            },
            {
              value: 'history',
              label: '历史',
              icon: 'message-square',
              badge: replyMessages.length > 0 ? replyMessages.length : undefined,
            },
            { value: 'original', label: '原文', icon: 'mail' },
          ]}
        />
      </div>

      {/* v1.36+: 桌面 3 列同时显示 (lg+), 不依赖 TabPanel 状态.
          之前 TabPanel line 158 'if (!active) return null' + className lg:block 不生效,
          导致桌面只能看到 1 列 (active 那个), 询盘原文 (col 3) 看不到. */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* v1.36+: 桌面 3 列同时显示 (lg+), 不依赖 TabPanel 状态.
            之前 TabPanel line 158 'if (!active) return null' + className lg:block 不生效,
            导致桌面只能看到 1 列 (active 那个), 询盘原文 (col 3) 看不到. */}
        <div className="hidden lg:block">
          {/* 桌面: 起草回复 (col 1) */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="send" size="sm" className="text-accent" />
              起草回复
            </h2>

            {feishuDrafts.length > 0 && (
              <Card padding="sm">
                <div className="text-xs text-ink-3 mb-2 flex items-center gap-1">
                  <Icon name="message-square" size="xs" />
                  飞书草稿 ({feishuDrafts.length})
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {feishuDrafts.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`text-left border rounded p-2 cursor-pointer transition-colors w-full focus:outline-none focus:ring-2 focus:ring-accent ${
                        selectedDraftId === d.id
                          ? 'border-accent bg-accent/5'
                          : 'border-line hover:border-accent/50'
                      }`}
                      onClick={() => {
                        setSelectedDraftId(d.id);
                        setEditedSubject(d.subject ?? '');
                        setEditedBody(d.body);
                        setToast({ kind: 'success', msg: '已选用飞书草稿' });
                      }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] text-ink-3">
                          🟦 飞书 · {d.language.toUpperCase()} · {d.generatedBy}
                        </span>
                        <span className="text-[10px] text-ink-3">{formatDate(d.createdAt)}</span>
                      </div>
                      <div className="text-xs font-medium truncate">{d.subject || '(无主题)'}</div>
                      <div className="text-[10px] text-ink-3 line-clamp-2 mt-0.5">{d.body}</div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            <Card padding="md">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <label htmlFor="draft-lang" className="text-xs text-ink-3">
                  语言:
                </label>
                <select
                  id="draft-lang"
                  value={draftLang}
                  onChange={(e) => setDraftLang(e.target.value as Language)}
                  className="px-2 py-1 border border-line rounded text-xs bg-surface"
                >
                  <option value="ja">日本語</option>
                  <option value="en">English</option>
                  <option value="zh">中文</option>
                </select>
                <label htmlFor="draft-tone" className="text-xs text-ink-3 ml-2">
                  语气:
                </label>
                <select
                  id="draft-tone"
                  value={draftTone}
                  onChange={(e) => setDraftTone(e.target.value as 'professional' | 'friendly')}
                  className="px-2 py-1 border border-line rounded text-xs bg-surface"
                >
                  <option value="professional">正式</option>
                  <option value="friendly">友好</option>
                </select>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="sparkles"
                  onClick={() => draftMut.mutate()}
                  loading={draftMut.isPending}
                  className="ml-auto"
                >
                  {draftMut.isPending ? '起草中 3-5s' : 'Trade OS AI 起草'}
                </Button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    placeholder="邮件主题"
                    className="flex-1 px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15"
                  />
                  {/* v1.36+: 再翻译按钮 (主题) */}
                  {editedSubject && (
                    <button
                      type="button"
                      onClick={() => {
                        inquiryApi
                          .translateText(inquiryId, { target: draftLang, text: editedSubject })
                          .then((res) => {
                            if (res.ok) setEditedSubject(res.translated);
                          });
                      }}
                      className="px-2 py-1 text-xs border border-line rounded hover:bg-surface-2"
                      title={`再翻译成${draftLang === 'ja' ? '日文' : draftLang === 'en' ? '英文' : '中文'}`}
                    >
                      🔄 译
                    </button>
                  )}
                </div>
                <div className="relative">
                  <textarea
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    placeholder="邮件正文"
                    rows={10}
                    className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 font-mono leading-relaxed"
                  />
                  {/* v1.36+: 再翻译按钮 (正文, 浮在右下角) */}
                  {editedBody && (
                    <button
                      type="button"
                      onClick={() => retransMut.mutate({ target: draftLang, text: editedBody })}
                      disabled={retransMut.isPending}
                      className="absolute bottom-2 right-2 px-2 py-1 text-xs bg-surface border border-line rounded hover:bg-accent hover:text-white disabled:opacity-50"
                      title={`把编辑内容重新翻译成${draftLang === 'ja' ? '日文' : draftLang === 'en' ? '英文' : '中文'}`}
                    >
                      {retransMut.isPending ? '⏳ 译中' : '🔄 再翻译'}
                    </button>
                  )}
                </div>
              </div>

              {/* v1.36+: 中文对照 — 让 Ken 看懂 AI 起草了什么 (默认展开) */}
              {draftChineseRef && (
                <details open className="mt-2 text-xs">
                  <summary className="cursor-pointer text-ink-3 hover:text-accent inline-flex items-center gap-1">
                    <Icon name="languages" size="xs" />
                    中文对照 (AI 起草了什么)
                  </summary>
                  <div className="mt-2 p-3 bg-surface-2 border border-accent/30 rounded text-sm text-ink-2 space-y-2">
                    {draftChineseRef.subject && (
                      <div>
                        <span className="text-ink-3 text-xs">📧 主题 (中文):</span>
                        <div className="font-medium text-ink mt-0.5">{draftChineseRef.subject}</div>
                      </div>
                    )}
                    {draftChineseRef.body && (
                      <div>
                        <span className="text-ink-3 text-xs">📄 正文 (中文):</span>
                        <div className="whitespace-pre-wrap text-ink mt-0.5 leading-relaxed">
                          {draftChineseRef.body}
                        </div>
                      </div>
                    )}
                    <div className="text-[10px] text-ink-3 mt-1 pt-1 border-t border-line">
                      📝 中文参考。AI 用 {draftLang} 起草, 这份是中文翻译。
                    </div>
                  </div>
                </details>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {mailto && (
                  <a
                    href={mailto}
                    className="px-4 py-2 bg-good text-white rounded text-sm font-medium hover:bg-good/90 inline-flex items-center gap-1"
                  >
                    <Icon name="mail" size="sm" strokeWidth={2} />
                    打开邮件客户端发送
                  </a>
                )}
                {!customerEmail && (
                  <span className="text-xs text-ink-3">客户无邮箱, 需手动联系</span>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft="check"
                  onClick={() => markMut.mutate()}
                  loading={markMut.isPending}
                >
                  标记已回复
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft="copy"
                  onClick={() => navigator.clipboard.writeText(editedBody)}
                  disabled={!editedBody}
                >
                  复制正文
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <div className="hidden lg:block">
          {/* 桌面: 客户信息 + 回复历史 + AI 提取 (col 2) */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="message-square" size="sm" className="text-accent" />
              客户信息 + 回复历史
            </h2>

            {customer ? (
              <Card padding="md">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <Icon name="building-2" size="sm" className="text-accent" />
                  关联客户
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">
                      公司
                    </div>
                    <Link
                      to="/customers/$customerId"
                      params={{ customerId: customer.id }}
                      className="font-medium hover:text-accent break-words"
                    >
                      {customer.name}
                    </Link>
                  </div>
                  <div>
                    <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">
                      国家
                    </div>
                    <div className="font-medium">{customer.country}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">
                      客户邮箱
                    </div>
                    {customerEmail ? (
                      <a
                        href={`mailto:${customerEmail}`}
                        className="text-accent hover:underline break-all"
                      >
                        {customerEmail}
                      </a>
                    ) : (
                      <div className="text-ink-3">未填</div>
                    )}
                    {/* v1.37.1: 登录账号 ≠ 发件 FROM，容易误解。
                        实际发件 FROM 由发送方式决定：真发送 = RESEND_FROM，
                        系统客户端 = OS 里配的 ken@toei-sosan.com。所以改成讲清"发出去长什么样"。 */}
                    <div className="text-[10px] text-ink-3 mt-1 leading-relaxed">
                      📮 真发送 FROM:{' '}
                      <span className="font-mono text-ink-2">ken@toei-sosan.com</span>
                      <br />
                      客户回复会进 Lark 收件箱
                    </div>
                    {currentUserEmail && !currentUserEmail.endsWith('@toei-sosan.com') && (
                      <div className="text-[10px] text-ink-3 mt-1 leading-relaxed">
                        登录账号 <span className="font-mono">{currentUserEmail}</span>{' '}
                        只影响系统权限，不影响发件人
                      </div>
                    )}
                  </div>
                  {/* v1.36+: 显示电话 (customer.phone) */}
                  {(customer as { phone?: string | null }).phone && (
                    <div>
                      <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">
                        电话
                      </div>
                      <a
                        href={`tel:${(customer as { phone?: string | null }).phone}`}
                        className="text-accent hover:underline break-all font-medium"
                      >
                        {(customer as { phone?: string | null }).phone}
                      </a>
                    </div>
                  )}
                  {customer.website && (
                    <div>
                      <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">
                        网站
                      </div>
                      <a
                        href={customer.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline break-all"
                      >
                        {customer.website}
                      </a>
                    </div>
                  )}
                  {/* v1.36+: 显示全部 tags (而不是顶部只显示 3 个) */}
                  {customer.tags && Array.isArray(customer.tags) && customer.tags.length > 3 && (
                    <div className="sm:col-span-2">
                      <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-1">
                        全部标签
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {customer.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-block px-1.5 py-0.5 bg-surface-2 rounded text-[10px] text-ink-3"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <Card padding="md" className="border-dashed border-accent/40">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Icon name="building-2" size="sm" className="text-ink-3" />
                  关联客户
                </h3>
                <p className="text-xs text-ink-3 mb-3">该询盘尚未关联客户, 一键创建并关联。</p>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="user-plus"
                  onClick={openConvertForm}
                  loading={convertMut.isPending}
                >
                  {convertMut.isPending ? '创建中…' : '一键加客户'}
                </Button>
              </Card>
            )}

            <LarkMailEntry customerEmail={customerEmail} />

            {replyMessages.length > 0 && (
              <Card padding="md">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <Icon name="message-circle" size="sm" className="text-accent" />
                  回复记录 ({replyMessages.length})
                </h3>
                <div className="space-y-3">
                  {replyMessages.map((m) => (
                    <div
                      key={m.id}
                      className={`p-3 rounded ${
                        m.direction === 'outbound'
                          ? 'bg-info/5 border border-info/20 ml-4'
                          : 'bg-surface-2 border border-line mr-4'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] text-ink-3">
                          {m.direction === 'outbound' ? '📤 我们' : '📥 客户'} · {m.source}
                        </span>
                        <span className="text-[10px] text-ink-3">{formatDate(m.createdAt)}</span>
                      </div>
                      {m.subject && <div className="text-xs font-medium mb-1">{m.subject}</div>}
                      <div className="text-xs text-ink-2 whitespace-pre-wrap break-words">
                        {m.body}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {hint && (
              <Card padding="md">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                  <Icon name="sparkles" size="sm" className="text-accent" />
                  AI 提取的产品需求
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                  {hint.category && <Field label="类目" value={String(hint.category)} />}
                  {hint.qty != null && <Field label="数量" value={String(hint.qty)} />}
                  {hint.craft && Array.isArray(hint.craft) && hint.craft.length > 0 && (
                    <Field label="工艺" value={hint.craft.join(', ')} />
                  )}
                  {hint.urgency && <Field label="紧急度" value={String(hint.urgency)} />}
                  {hint.budget != null && <Field label="预算" value={String(hint.budget)} />}
                  {hint.preferredContact && (
                    <Field label="偏好联系方式" value={String(hint.preferredContact)} />
                  )}
                </div>
                {/* v1.36+: 完整 rawText 显示 (AI 提取的原文片段) */}
                {hint.rawText && (
                  <div className="mt-3 pt-3 border-t border-line">
                    <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-1">
                      提取原文片段
                    </div>
                    <div className="text-xs text-ink-2 bg-surface-2 rounded p-2 whitespace-pre-wrap break-words">
                      {hint.rawText}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>

        <div className="hidden lg:block">
          {/* 桌面: 询盘原文 (col 3) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <Icon name="mail" size="sm" className="text-accent" />
                询盘原文
              </h2>
              {/* v1.36+: 翻译按钮组 (中/英/日) */}
              <div className="inline-flex rounded border border-line bg-surface">
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'zh' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] rounded-l hover:bg-surface-2 disabled:opacity-50"
                  title="翻译成中文"
                >
                  🇨🇳 中
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'en' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] border-l border-line hover:bg-surface-2 disabled:opacity-50"
                  title="翻译成英文"
                >
                  🇺🇸 英
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'ja' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] rounded-r border-l border-line hover:bg-surface-2 disabled:opacity-50"
                  title="翻译成日文"
                >
                  🇯🇵 日
                </button>
              </div>
            </div>
            <Card padding="md">
              <div className="bg-surface-2 rounded p-3 text-sm whitespace-pre-wrap break-words leading-relaxed">
                {inquiry.body || '(无内容)'}
              </div>
              {inquiry.sourceRef && (
                <div className="text-[10px] text-ink-3 mt-2">来源 ID: {inquiry.sourceRef}</div>
              )}
              {/* v1.36+: 翻译结果区 */}
              {translateMut.isPending && (
                <div className="mt-3 pt-3 border-t border-line">
                  <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <span className="animate-pulse">⏳</span>
                    翻译中 (Qwen3-30B, 3-5s)...
                  </div>
                </div>
              )}
              {translatedBody && !translateMut.isPending && (
                <div className="mt-3 pt-3 border-t border-line">
                  <div className="text-[10px] text-accent uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Icon name="languages" size="xs" />
                    译文 (
                    {translatedBody.target === 'ja'
                      ? '🇯🇵 日文'
                      : translatedBody.target === 'en'
                        ? '🇺🇸 英文'
                        : '🇨🇳 中文'}
                    )
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(translatedBody.text)}
                      className="ml-auto text-ink-3 hover:text-accent text-[10px] inline-flex items-center gap-0.5"
                    >
                      <Icon name="copy" size="xs" />
                      复制
                    </button>
                    <button
                      type="button"
                      onClick={() => setTranslatedBody(null)}
                      className="text-ink-3 hover:text-bad text-[10px]"
                      title="关闭"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="bg-accent/5 border border-accent/20 rounded p-3 text-sm whitespace-pre-wrap break-words leading-relaxed">
                    {translatedBody.text}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* v1.36+: 移动端 Tab 切换 (sm:hidden) — 桌面 3 列同时显示, 移动单列 + Tab */}
      <div className="lg:hidden">
        <TabPanel value="compose" active={tab === 'compose'}>
          {/* 移动端草稿区 - 简版 (复用桌面版的 Card) */}
          <Card padding="md">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select
                value={draftLang}
                onChange={(e) => setDraftLang(e.target.value as Language)}
                className="px-2 py-1 border border-line rounded text-xs bg-surface"
              >
                <option value="ja">日本語</option>
                <option value="en">English</option>
                <option value="zh">中文</option>
              </select>
              <select
                value={draftTone}
                onChange={(e) => setDraftTone(e.target.value as 'professional' | 'friendly')}
                className="px-2 py-1 border border-line rounded text-xs bg-surface"
              >
                <option value="professional">正式</option>
                <option value="friendly">友好</option>
              </select>
              <Button
                variant="primary"
                size="sm"
                iconLeft="sparkles"
                onClick={() => draftMut.mutate()}
                loading={draftMut.isPending}
              >
                {draftMut.isPending ? '起草中' : 'AI 起草'}
              </Button>
            </div>
            <input
              type="text"
              value={editedSubject}
              onChange={(e) => setEditedSubject(e.target.value)}
              placeholder="邮件主题"
              className="w-full mb-2 px-3 py-2 border border-line rounded text-sm bg-surface"
            />
            <textarea
              value={editedBody}
              onChange={(e) => setEditedBody(e.target.value)}
              placeholder="邮件正文"
              rows={6}
              className="w-full mb-2 px-3 py-2 border border-line rounded text-sm bg-surface"
            />
            {/* v1.37.12: 移动端同步移除「真发送」+ toOverride + Lark Mail 按钮。
                v1.37.13: 加「复制主题/正文」— mailto 在 Apple Mail 上会丢换行,
                复制粘贴是唯一 100% 可靠的路径。 */}
            <div className="flex flex-wrap gap-2 mb-2">
              {mailto && (
                <a
                  href={mailto}
                  className="flex-1 min-w-[140px] px-4 py-2 bg-good/10 text-good border border-good/30 rounded text-sm font-medium hover:bg-good/20 inline-flex items-center justify-center gap-1"
                >
                  📨 打开邮件客户端
                </a>
              )}
              {editedSubject && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(editedSubject);
                      setToast({ kind: 'success', msg: '📋 主题已复制' });
                    } catch {
                      setToast({ kind: 'error', msg: '复制失败' });
                    }
                  }}
                  className="flex-1 min-w-[120px] px-4 py-2 bg-surface-2 border border-line rounded text-sm hover:bg-line/30"
                >
                  📋 复制主题
                </button>
              )}
              {editedBody && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(editedBody);
                      setToast({ kind: 'success', msg: '📋 正文已复制（直接粘贴）' });
                    } catch {
                      setToast({ kind: 'error', msg: '复制失败' });
                    }
                  }}
                  className="flex-1 min-w-[120px] px-4 py-2 bg-surface-2 border border-line rounded text-sm hover:bg-line/30"
                >
                  📋 复制正文
                </button>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => markMut.mutate()}
              loading={markMut.isPending}
              className="w-full"
            >
              标记已回复
            </Button>
          </Card>
        </TabPanel>
        <TabPanel value="history" active={tab === 'history'}>
          <Card padding="md">
            <p className="text-sm text-ink-2 mb-3">
              关联客户 + 回复历史 — 桌面 3 列布局下查看更详细。
            </p>
            {customer && (
              <Link
                to="/customers/$customerId"
                params={{ customerId: customer.id }}
                className="text-sm text-accent hover:underline"
              >
                查看 {customer.name} 详情 →
              </Link>
            )}
          </Card>
        </TabPanel>
        <TabPanel value="original" active={tab === 'original'}>
          <Card padding="md">
            {/* v1.36+: 移动端翻译按钮组 */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="text-xs text-ink-3">📩 询盘原文</span>
              <div className="inline-flex rounded border border-line bg-surface">
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'zh' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] rounded-l hover:bg-surface-2 disabled:opacity-50"
                >
                  🇨🇳 中
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'en' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] border-l border-line hover:bg-surface-2 disabled:opacity-50"
                >
                  🇺🇸 英
                </button>
                <button
                  type="button"
                  onClick={() => translateMut.mutate({ target: 'ja' })}
                  disabled={translateMut.isPending || !inquiry.body}
                  className="px-2 py-1 text-[10px] rounded-r border-l border-line hover:bg-surface-2 disabled:opacity-50"
                >
                  🇯🇵 日
                </button>
              </div>
            </div>
            <div className="bg-surface-2 rounded p-3 text-sm whitespace-pre-wrap break-words leading-relaxed">
              {inquiry.body || '(无内容)'}
            </div>
            {inquiry.sourceRef && (
              <div className="text-[10px] text-ink-3 mt-2">来源 ID: {inquiry.sourceRef}</div>
            )}
            {/* 移动端翻译结果 */}
            {translateMut.isPending && (
              <div className="mt-3 text-[10px] text-ink-3">⏳ 翻译中...</div>
            )}
            {translatedBody && !translateMut.isPending && (
              <div className="mt-3 pt-3 border-t border-line">
                <div className="text-[10px] text-accent mb-1">
                  译文 (
                  {translatedBody.target === 'ja'
                    ? '🇯🇵 日文'
                    : translatedBody.target === 'en'
                      ? '🇺🇸 英文'
                      : '🇨🇳 中文'}
                  )
                </div>
                <div className="bg-accent/5 border border-accent/20 rounded p-3 text-sm whitespace-pre-wrap break-words">
                  {translatedBody.text}
                </div>
              </div>
            )}
          </Card>
        </TabPanel>
      </div>

      {/* v1.32 P1-10: 一键加客户 — pre-fill 表单 modal */}
      {showConvertForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card padding="md" className="w-full max-w-md bg-surface">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <Icon name="user-plus" size="sm" className="text-accent" />
              转客户 (预填自询盘, 可改)
            </h3>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs text-ink-2">公司名 *</span>
                <input
                  className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                  value={convertForm.name}
                  onChange={(e) => setConvertForm({ ...convertForm, name: e.target.value })}
                  required
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs text-ink-2">国家 (2字符)</span>
                  <input
                    className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                    value={convertForm.country}
                    maxLength={2}
                    onChange={(e) =>
                      setConvertForm({ ...convertForm, country: e.target.value.toUpperCase() })
                    }
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-ink-2">语言</span>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                    value={convertForm.language}
                    onChange={(e) => setConvertForm({ ...convertForm, language: e.target.value })}
                  >
                    <option value="en">English</option>
                    <option value="ja">日本語</option>
                    <option value="zh">中文</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-ink-2">邮箱</span>
                <input
                  type="email"
                  className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                  value={convertForm.email}
                  onChange={(e) => setConvertForm({ ...convertForm, email: e.target.value })}
                  placeholder="buyer@example.com"
                />
              </label>
              <label className="block">
                <span className="text-xs text-ink-2">官网</span>
                <input
                  type="url"
                  className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                  value={convertForm.website}
                  onChange={(e) => setConvertForm({ ...convertForm, website: e.target.value })}
                  placeholder="https://"
                />
              </label>
              <label className="block">
                <span className="text-xs text-ink-2">来源</span>
                <input
                  className="w-full mt-1 px-3 py-2 border border-line rounded text-sm"
                  value={convertForm.source}
                  onChange={(e) => setConvertForm({ ...convertForm, source: e.target.value })}
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setShowConvertForm(false)}>
                取消
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => convertMut.mutate()}
                loading={convertMut.isPending}
                disabled={!convertForm.name}
              >
                {convertMut.isPending ? '创建中…' : '确认创建'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {toast && (
        <button
          type="button"
          className={`fixed bottom-4 right-4 px-4 py-2 rounded text-sm shadow-lg z-50 cursor-pointer ${toast.kind === 'success' ? 'bg-good text-white' : 'bg-bad text-white'}`}
          onClick={() => setToast(null)}
        >
          {toast.msg}
        </button>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] text-ink-3 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}
